/**
 * Snowplow Analytics Initialization
 *
 * Initializes the Snowplow browser tracker for event tracking.
 */

import { getSessionLayoutVersion } from '@/lib/analytics/session-layout-registry';
import type { SessionPageV2EnrollmentSource } from '@/models/analytics/features/user';
import { isTauri } from '@/tauri/utils';
import { AppType } from '@/types';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { getDesktopBuildId, getDesktopVersion } from '@/utils/appVersion';
import { isOnPrem, isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { getDesktopOsVersion } from '@/utils/desktopOsInfo';
import { clearActiveTeam, getAnalyticsContext } from '@heidi/analytics';
import { getWorkspaceId } from '@heidi/workspace/data';
import {
  addGlobalContexts,
  clearUserData,
  flushBuffer,
  newTracker,
  setUserId,
} from '@snowplow/browser-tracker';

import { SNOWPLOW_TRACKER_NAMESPACE } from './constants';

// Re-exported so existing `@/lib/snowplow` importers keep working. The canonical
// definition lives in the SDK-free `./constants` module so consumers that only
// need the namespace literal (e.g. the install-source orchestrator) don't pull
// `@snowplow/browser-tracker` into their graph — on-prem F1 guardrail.
export { SNOWPLOW_TRACKER_NAMESPACE };

// Local copies of getAppType/getPlatform — trivial isTauri() wrappers also
// defined in `@/models/analyticsScribeModel`. We deliberately do NOT import
// them from there: importing the Snowplow barrel must stay cheap so consumers
// like `authModel` (and every test that transitively imports it) don't drag in
// the analytics/audio model graph and its module-load logger side-effects.
// Mirrors the same local-copy pattern in `posthog-provider.tsx`.
const getAppType = (): AppType => (isTauri() ? AppType.Desktop : AppType.Web);
const getPlatform = (): 'web' | 'desktop' => (isTauri() ? 'desktop' : 'web');

let isInitialized = false;

/**
 * Post-boot fields of the `client_context` global context — only known after
 * the user authenticates. Set via {@link setSnowplowClientContext} from the
 * identify effect and re-read on every event by the context generator below.
 *
 * `update_channel` (APP-6116) is the desktop auto-update channel derived from
 * the authenticated region — null on web and before login. (`is_managed`, the
 * other post-boot field in the schema, is not wired on the FE yet; when it is,
 * it joins this holder + the setter.)
 */
let postBootClientContext: { update_channel: string | null } = {
  update_channel: null,
};

/**
 * Authenticated user-level context attached to every subsequent Snowplow event.
 * It stays absent before identify rather than treating an unresolved user as
 * unenrolled, and is cleared on logout so shared machines cannot inherit it.
 */
let postBootUserContext: {
  session_page_v2_enrollment_source: SessionPageV2EnrollmentSource | null;
} | null = null;

/**
 * localStorage key holding the last-known business `user_id`.
 *
 * Snowplow's `setUserId` lives only in the in-memory tracker — unlike Amplitude
 * and PostHog, whose SDKs persist identity to storage and re-attach it on
 * `init()` before the first event. Every web page load therefore re-creates an
 * anonymous tracker, so events that fire before the React identify effect runs
 * (the first `page_view`, early `error_viewed`, anything on `/`) go out with a
 * `domain_userid` but no `user_id`. We mirror the Amplitude/PostHog pattern
 * manually: persist on set, rehydrate on init, clear on logout.
 */
const SNOWPLOW_USER_ID_STORAGE_KEY = 'heidi_snowplow_user_id';

function persistSnowplowUserId(userId: string): void {
  try {
    window.localStorage.setItem(SNOWPLOW_USER_ID_STORAGE_KEY, userId);
  } catch {
    // localStorage can throw (private mode / disabled) — identity persistence
    // is best-effort and must never break tracking.
  }
}

function readPersistedSnowplowUserId(): string | null {
  try {
    return window.localStorage.getItem(SNOWPLOW_USER_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearPersistedSnowplowUserId(): void {
  try {
    window.localStorage.removeItem(SNOWPLOW_USER_ID_STORAGE_KEY);
  } catch {
    // best-effort — see persistSnowplowUserId
  }
}

export function initSnowplow() {
  // TELEMETRY-GATE (design D5): compound, and the only one in the migration
  // set. Do NOT collapse this to `!isSaasTelemetryEnabled` — the axis cascades
  // to off on-prem, and that would silence the in-appliance same-origin
  // collector selected below, which is not SaaS egress. This gate suppresses
  // only the SaaS-collector posture: a cloud build with telemetry off.
  if (!isSaasTelemetryEnabled && !isOnPrem) {
    return;
  }

  if (isInitialized) {
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  // Collector URL.
  // - On-prem: ALWAYS same-origin (`window.location.origin`), reached through
  //   the Traefik ingress (which injects the Authorization header — so no API
  //   key is baked into the browser bundle). We deliberately do NOT honor an
  //   absolute override here: a stray/misconfigured external URL would be an
  //   off-appliance egress vector in a hospital network (on-prem guardrail F1,
  //   documented exception). This mirrors how the rest of the on-prem client
  //   resolves the appliance host — `endpoints.ts` (API), the WS URL, and the
  //   axios clients all use `window.location.origin` on-prem. Path routing is
  //   handled by `postPath` + Traefik below.
  // - Cloud: the configured cloud collector (unchanged).
  const collectorUrl = isOnPrem
    ? window.location.origin
    : process.env.NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL;

  if (!collectorUrl) {
    return;
  }

  try {
    newTracker(SNOWPLOW_TRACKER_NAMESPACE, collectorUrl, {
      appId: 'com.heidi.scribe_frontend',
      platform: isTauri() ? 'pc' : 'web',
      contexts: {
        webPage: true,
        session: true,
      },
      cookieName: 'heidi_snowplow',
      // Share the domain_userid cookie across the root domain so a visitor keeps
      // one anonymous id across the marketing site (www.heidihealth.com) and the
      // app (scribe.heidihealth.com). Must match the marketing GTM tracker's
      // cookieName + cookieDomain exactly or no linking forms. CLOUD WEB ONLY:
      // Tauri (custom-scheme origin) and on-prem (hospital-internal host) are not
      // served from heidihealth.com, so a .heidihealth.com cookie is rejected by
      // the browser and domain_userid regenerates every load, breaking
      // sessionization. Both fall back to a host-only cookie.
      cookieDomain: !isTauri() && !isOnPrem ? '.heidihealth.com' : undefined,
      cookieLifetime: 63072000, // 2 years
      cookieSameSite: 'Lax',
      anonymousTracking: false,
      credentials: isTauri() ? 'omit' : 'include',
      // `keepalive` makes the POST use fetch's keepalive flag, which survives
      // page unload — so the pagehide flush below delivers the tail instead of
      // the browser cancelling an in-flight request. Enabled on BOTH cloud and
      // on-prem: without it, events fired just before the user closes or
      // navigates are silently dropped, while their PostHog twins survive
      // (PostHog's SDK already uses keepalive/sendBeacon). This matters most for
      // message_received, which fires after a multi-second Ask-Heidi stream —
      // exactly when the user tends to close or switch away.
      // Only on-prem batches (bufferSize 10) to cut request volume to the
      // in-cluster collector. Cloud keeps immediate send: keepalive caps total
      // in-flight body at 64KB, which a large batch could exceed, so do NOT add
      // bufferSize on cloud.
      ...(isOnPrem
        ? { postPath: '/v1/ingest/tp2', bufferSize: 10, keepalive: true }
        : { keepalive: true }),
    });

    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Snowplow:', error);
  }

  if (isInitialized) {
    try {
      // Attached as a context GENERATOR (a function), not a static entity, so
      // the post-boot fields in `postBootClientContext` (set after login via
      // setSnowplowClientContext) are re-read on every event. The static fields
      // are stable for the session, so re-evaluating them per event is a no-op.
      addGlobalContexts([
        () => ({
          schema: 'iglu:com.heidi/client_context/jsonschema/1-0-3',
          data: {
            client_platform: getAppType(),
            platform: getPlatform(),
            desktop_version: getDesktopVersion() ?? null,
            // PRODA-174: native host OS version from Tauri os_info; null on web
            // and before the desktop IPC cache resolves. Not the app semver.
            desktop_os_version: getDesktopOsVersion() ?? null,
            installer_type: getDesktopInstallerType(),
            // APP-6116: baked CI short SHA, static at boot like desktop_version;
            // null on web and local / non-publish desktop builds.
            build_id: getDesktopBuildId() ?? null,
            // APP-6116: region-derived desktop update channel, re-attached
            // post-login via setSnowplowClientContext; null on web / pre-login.
            update_channel: postBootClientContext.update_channel,
          },
        }),
        // The enrichment pipeline puts `layout_version` on every leg EXCEPT
        // Snowplow, which takes raw caller props — so without this entity no
        // Snowplow event can be segmented V1 vs V2. Undefined off the session
        // route skips attachment.
        () => {
          const layoutVersion = getSessionLayoutVersion();
          if (!layoutVersion) return undefined;
          return {
            schema: 'iglu:com.heidi/session_layout/jsonschema/1-0-0',
            data: { layout_version: layoutVersion },
          };
        },
        // SCR-7301: persisted enrollment origin is user identity, not an event
        // action. Attach it as the governed user_context entity so every
        // post-identify Snowplow event can be segmented by the same cohort as
        // the profile-oriented destinations.
        () => {
          if (!postBootUserContext) return undefined;
          return {
            schema: 'iglu:com.heidi/user_context/jsonschema/1-0-1',
            data: postBootUserContext,
          };
        },
        // Active workspace/team the event fired in the context of, read per event
        // from the two live sources the Amplitude plugin also reads (so the two
        // pipelines can't drift): getWorkspaceId() and the active teamId from the
        // analytics context registry (set by setActiveTeam for the current
        // workspace). Skipped only while both are unknown.
        () => {
          const workspace_id = getWorkspaceId() ?? null;
          const team_id = getAnalyticsContext().teamId ?? null;
          if (!workspace_id && !team_id) return undefined;
          return {
            schema: 'iglu:com.heidi/workspace_context/jsonschema/1-0-0',
            data: { workspace_id, team_id },
          };
        },
      ]);
    } catch (error) {
      console.error('Failed to attach Snowplow client context:', error);
    }

    // Re-attach the last-known user_id before any event fires (the first
    // page_view runs in a mount effect, after this synchronous boot call), so
    // an already-authenticated session carries user_id from event #1 instead of
    // staying anonymous until the React identify effect re-runs. Mirrors the
    // auto-rehydrate Amplitude/PostHog get from their SDKs.
    const persistedUserId = readPersistedSnowplowUserId();
    if (persistedUserId) {
      try {
        setUserId(persistedUserId);
      } catch (error) {
        console.error('Failed to rehydrate Snowplow user id:', error);
      }
    }

    // Flush any buffered events before the page is hidden/unloaded so the tail
    // isn't lost on navigation or close. Paired with `keepalive` above, the
    // flushed POST actually completes during unload. `persisted` means a bfcache
    // entry that may be restored — don't flush then. Registered on both cloud
    // and on-prem: cloud sends immediately so this is usually a no-op, but the
    // guarantee costs nothing and covers any in-flight tail.
    window.addEventListener('pagehide', (event) => {
      if (!event.persisted) {
        flushBuffer();
      }
    });
  }
}

export function isSnowplowInitialized(): boolean {
  return isInitialized;
}

export function setSnowplowUserId(userId: string): void {
  if (!isInitialized) return;
  setUserId(userId);
  // Persist so the next page load can re-attach it in initSnowplow before the
  // first event, instead of waiting for the React identify effect.
  persistSnowplowUserId(userId);
}

/**
 * Sets the post-boot fields of the Snowplow `client_context` global context
 * (APP-6116). Called from the identify effect once the authenticated region is
 * known, so every subsequent event's client_context carries the desktop
 * `update_channel` (null on web / pre-login). Safe to call before init — the
 * context generator reads the holder the next time it fires.
 */
export function setSnowplowClientContext(fields: {
  update_channel?: string | null;
}): void {
  postBootClientContext = { ...postBootClientContext, ...fields };
}

/**
 * Sets the authenticated Snowplow `user_context` fields. Safe before tracker
 * initialization: the global-context generator reads this holder per event.
 */
export function setSnowplowUserContext(fields: {
  session_page_v2_enrollment_source: SessionPageV2EnrollmentSource | null;
}): void {
  postBootUserContext = fields;
}

/**
 * Clears Snowplow identity on logout. Resets the business `user_id` *and*
 * regenerates the anonymous `domain_userid` + session via Snowplow's
 * `clearUserData`, mirroring `resetAmplitude()` / `invalidatePosthogFlags()`
 * so logout resets identity consistently across every destination — and a
 * shared machine never attributes the next user's events to the previous one.
 * Also drops the persisted user_id so it is not rehydrated on the next boot.
 * No-op until the tracker is initialized (on-prem / SSR / no collector URL).
 */
export function clearSnowplowUserData(): void {
  // Always drop the persisted id, even pre-init, so a logged-out identity can
  // never be rehydrated by a later initSnowplow on the same machine.
  clearPersistedSnowplowUserId();
  // Drop post-boot contexts so a shared machine's next (anonymous / next-user)
  // session cannot inherit the previous user's update channel or enrollment.
  postBootClientContext = { update_channel: null };
  postBootUserContext = null;
  // Clear the active team held in the analytics-context registry. workspace_id is
  // cleared elsewhere on logout (clearLogoutStorage), but the team id has no other
  // production reset — without this the workspace_context generator would keep
  // attaching the previous user's team_id to anonymous / next-user events.
  clearActiveTeam();
  if (!isInitialized) return;
  clearUserData();
}
